import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { API } from "@/utils/api";
import { toast } from "sonner";
import {
  Upload, Download, FileText, CheckCircle2, AlertCircle, Loader2,
  Search, X, Copy, RotateCcw, Eye,
} from "lucide-react";
import CsvFormatReferenceCard from "@/components/CsvFormatReferenceCard";

export default function BulkUpload() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  // Customer
  const [custQ, setCustQ] = useState("");
  const [custOpen, setCustOpen] = useState(false);
  const [custOptions, setCustOptions] = useState([]);
  const [customer, setCustomer] = useState(null);

  // File
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  // Validation
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null);
  const [showAllValid, setShowAllValid] = useState(false);

  // Confirm
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null);

  // Jobs history
  const [jobs, setJobs] = useState([]);
  const [jobDetail, setJobDetail] = useState(null);

  useEffect(() => {
    if (!custOpen) return;
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/v2/customer-profiles/search?q=${encodeURIComponent(custQ)}`);
        setCustOptions(r.data?.results || []);
      } catch { setCustOptions([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [custQ, custOpen]);

  const loadJobs = useCallback(async () => {
    try {
      const r = await api.get("/v2/bulk-upload/jobs?limit=20");
      setJobs(r.data?.items || []);
    } catch { setJobs([]); }
  }, []);
  useEffect(() => { loadJobs(); }, [loadJobs]);

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem("sendtoo_token");
      const r = await fetch(`${API}/v2/bulk-upload/template`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Sendtoo_Bulk_Upload_Template.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Template download failed"); }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  };

  const validate = async () => {
    if (!customer || !file) {
      toast.error("Pick a customer and a CSV file");
      return;
    }
    setValidating(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("customer_id", customer.customer_id);
    try {
      const token = localStorage.getItem("sendtoo_token");
      const r = await fetch(`${API}/v2/bulk-upload/validate`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || "Validate failed");
      setValidation(data);
      setStep(2);
    } catch (e) {
      toast.error(e.message);
    } finally { setValidating(false); }
  };

  const confirm = async () => {
    if (!validation?.job_id) return;
    setConfirming(true);
    try {
      const r = await api.post(`/v2/bulk-upload/confirm/${validation.job_id}`);
      setResult(r.data);
      setStep(3);
      loadJobs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Confirm failed");
    } finally { setConfirming(false); }
  };

  const reset = () => {
    setStep(1);
    setFile(null);
    setValidation(null);
    setResult(null);
  };

  const copyAllLrs = async () => {
    const text = (result?.lr_numbers || []).join("\n");
    try {
      await navigator.clipboard.writeText(text);
      toast.success("LR numbers copied");
    } catch { toast.error("Clipboard unavailable"); }
  };

  const openJobDetail = async (jid) => {
    try {
      const r = await api.get(`/v2/bulk-upload/jobs/${jid}`);
      setJobDetail(r.data);
    } catch (e) { toast.error("Failed to load"); }
  };

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="bulk-upload-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Bulk Order Upload</h1>
          <p className="text-sm text-slate-500">Upload a CSV to create multiple shipments in one go</p>
        </div>

        {/* Stepper */}
        <div className="flex items-center gap-2">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center flex-1 last:flex-none">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                step >= s ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-400 border border-slate-200"
              }`}>{s}</div>
              <div className="ml-2 text-xs font-semibold text-slate-600">
                {s === 1 ? "Upload" : s === 2 ? "Validate" : "Create"}
              </div>
              {s < 3 && <div className={`flex-1 h-px mx-2 ${step > s ? "bg-emerald-500" : "bg-slate-200"}`} />}
            </div>
          ))}
        </div>

        {step === 1 && (
          <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
            {/* Customer */}
            <div>
              <label className="block text-xs font-bold uppercase text-slate-500 mb-1">Customer*</label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={custQ}
                  onFocus={() => setCustOpen(true)}
                  onChange={(e) => { setCustQ(e.target.value); setCustOpen(true); setCustomer(null); }}
                  onBlur={() => setTimeout(() => setCustOpen(false), 200)}
                  placeholder="Search customer…"
                  className="pl-8"
                  data-testid="bulk-customer-search"
                />
                {custOpen && custOptions.length > 0 && (
                  <div className="absolute z-10 top-11 left-0 right-0 max-h-60 overflow-auto bg-white border border-slate-200 rounded shadow-lg">
                    {custOptions.map((c) => (
                      <button
                        key={c.customer_id}
                        onClick={() => { setCustomer(c); setCustQ(c.company_name || c.contact_person); setCustOpen(false); }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b last:border-b-0"
                        data-testid={`bulk-cust-option-${c.customer_id}`}
                      >
                        <div className="font-semibold">{c.company_name || c.contact_person}</div>
                        <div className="text-xs text-slate-500">{c.email}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-btn">
              <Download className="w-4 h-4 mr-1" /> Download CSV Template
            </Button>

            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition ${
                dragOver ? "border-emerald-500 bg-emerald-50" : "border-slate-300 bg-slate-50 hover:border-slate-400"
              }`}
              onClick={() => document.getElementById("bulk-file-input").click()}
              data-testid="dropzone"
            >
              <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
              {file ? (
                <>
                  <div className="font-semibold text-slate-800">{file.name}</div>
                  <div className="text-xs text-slate-500">{(file.size / 1024).toFixed(1)} KB</div>
                </>
              ) : (
                <>
                  <p className="text-sm text-slate-600">Drop CSV here or click to browse</p>
                  <p className="text-xs text-slate-400 mt-1">.csv only</p>
                </>
              )}
              <input
                id="bulk-file-input"
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="file-input"
              />
            </div>

            <Button
              onClick={validate}
              disabled={!customer || !file || validating}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="validate-btn"
            >
              {validating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Upload & Validate
            </Button>
          </div>
        )}

        {step === 1 && (
          <CsvFormatReferenceCard onDownloadTemplate={downloadTemplate} />
        )}

        {step === 2 && validation && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Total Rows" value={validation.total_rows} />
              <Stat label="Valid" value={validation.valid_rows} color="emerald" />
              <Stat label="Errors" value={validation.error_rows} color="red" />
            </div>

            {validation.errors?.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="p-3 border-b border-slate-100 text-sm font-bold text-red-700">
                  {validation.errors.length} validation errors
                </div>
                <div className="max-h-64 overflow-auto">
                  <table className="w-full text-xs" data-testid="error-table">
                    <thead className="bg-red-50 text-red-800">
                      <tr>
                        <th className="text-left p-2">Row</th>
                        <th className="text-left p-2">Field</th>
                        <th className="text-left p-2">Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {validation.errors.map((e, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="p-2 font-mono">{e.row}</td>
                          <td className="p-2">{e.field}</td>
                          <td className="p-2">{e.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" onClick={reset} data-testid="reupload-btn">
                <RotateCcw className="w-4 h-4 mr-1" /> Re-upload
              </Button>
              {validation.valid_rows > 0 && (
                <Button
                  onClick={confirm}
                  disabled={confirming}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="confirm-create-btn"
                >
                  {confirming ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                  Confirm & Create {validation.valid_rows} Shipments
                </Button>
              )}
            </div>
          </div>
        )}

        {step === 3 && result && (
          <div className="bg-emerald-50 border border-emerald-300 rounded-xl p-5 space-y-4" data-testid="result-panel">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              <h3 className="text-xl font-bold text-emerald-800">
                {result.created_count} shipments created successfully
              </h3>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-bold uppercase text-slate-500">LR Numbers</span>
                <Button variant="ghost" size="sm" onClick={copyAllLrs} data-testid="copy-lrs-btn">
                  <Copy className="w-3.5 h-3.5 mr-1" /> Copy all
                </Button>
              </div>
              <div className="bg-white border border-slate-200 rounded p-2 max-h-40 overflow-auto font-mono text-xs">
                {(result.lr_numbers || []).map((l) => <div key={l}>{l}</div>)}
              </div>
            </div>
            {result.failures?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-800">
                {result.failures.length} failures: {result.failures.slice(0, 3).join("; ")}
              </div>
            )}
            <div className="flex gap-2">
              <Button onClick={reset} variant="outline" data-testid="upload-another-btn">Upload Another</Button>
              <Button onClick={() => navigate("/admin/shipments")} className="bg-slate-800 hover:bg-slate-900 text-white">
                View All Shipments
              </Button>
            </div>
          </div>
        )}

        {/* Jobs history */}
        <div className="bg-white border border-slate-200 rounded-xl">
          <div className="p-3 border-b border-slate-100 text-sm font-bold text-slate-800 flex justify-between items-center">
            <span>Upload Jobs History</span>
            <Button variant="ghost" size="sm" onClick={loadJobs}><RotateCcw className="w-3.5 h-3.5" /></Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="jobs-table">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3">File</th>
                  <th className="text-center p-3">Total</th>
                  <th className="text-center p-3">Valid</th>
                  <th className="text-center p-3">Errors</th>
                  <th className="text-center p-3">Status</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {jobs.length === 0 ? (
                  <tr><td colSpan="7" className="p-6 text-center text-slate-400">No jobs yet</td></tr>
                ) : (
                  jobs.map((j) => (
                    <tr key={j.job_id} className="hover:bg-slate-50">
                      <td className="p-3 text-xs">{new Date(j.created_at).toLocaleString("en-IN")}</td>
                      <td className="p-3 text-xs">{j.filename}</td>
                      <td className="p-3 text-center">{j.total_rows}</td>
                      <td className="p-3 text-center text-emerald-700 font-semibold">{j.valid_rows}</td>
                      <td className="p-3 text-center text-red-700 font-semibold">{j.error_rows}</td>
                      <td className="p-3 text-center"><StatusBadge s={j.status} /></td>
                      <td className="p-3 text-right">
                        <button onClick={() => openJobDetail(j.job_id)} className="text-blue-600 hover:underline text-xs" data-testid={`view-job-${j.job_id}`}>
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {jobDetail && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-3" onClick={() => setJobDetail(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl p-5 w-full max-w-2xl shadow-2xl max-h-[80vh] overflow-auto" data-testid="job-detail-modal">
            <div className="flex justify-between items-start mb-3">
              <h3 className="text-lg font-bold text-slate-800">Job {jobDetail.job_id}</h3>
              <button onClick={() => setJobDetail(null)}><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-3 text-xs">
              <Stat label="Total" value={jobDetail.total_rows} />
              <Stat label="Valid" value={jobDetail.valid_rows} color="emerald" />
              <Stat label="Errors" value={jobDetail.error_rows} color="red" />
            </div>
            {jobDetail.errors?.length > 0 && (
              <div className="border border-slate-200 rounded overflow-auto max-h-64 text-xs">
                <table className="w-full">
                  <thead className="bg-red-50 text-red-800 sticky top-0"><tr><th className="text-left p-2">Row</th><th className="text-left p-2">Field</th><th className="text-left p-2">Error</th></tr></thead>
                  <tbody>{jobDetail.errors.map((e, i) => (<tr key={i} className="border-t"><td className="p-2 font-mono">{e.row}</td><td className="p-2">{e.field}</td><td className="p-2">{e.message}</td></tr>))}</tbody>
                </table>
              </div>
            )}
            {jobDetail.created_shipment_ids?.length > 0 && (
              <div className="mt-3 text-xs">
                <strong>{jobDetail.created_shipment_ids.length}</strong> shipments created
              </div>
            )}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

function Stat({ label, value, color = "slate" }) {
  const c = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    red: "bg-red-50 border-red-200 text-red-700",
    slate: "bg-slate-50 border-slate-200 text-slate-700",
  }[color];
  return (
    <div className={`border rounded-lg p-3 text-center ${c}`}>
      <div className="text-[10px] uppercase font-bold opacity-80">{label}</div>
      <div className="text-xl font-bold mt-0.5">{value ?? 0}</div>
    </div>
  );
}

function StatusBadge({ s }) {
  const colors = {
    VALID: "bg-emerald-100 text-emerald-700 border-emerald-200",
    COMPLETED: "bg-emerald-100 text-emerald-700 border-emerald-200",
    PROCESSING: "bg-blue-100 text-blue-700 border-blue-200",
    PARTIAL: "bg-amber-100 text-amber-700 border-amber-200",
    FAILED: "bg-red-100 text-red-700 border-red-200",
    VALIDATING: "bg-slate-100 text-slate-600 border-slate-200",
  };
  return (
    <span className={`inline-block text-[10px] font-bold px-2 py-1 rounded border ${colors[s] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
      {s}
    </span>
  );
}
